import { useEffect, useRef } from 'react';
import { useStateFields } from '../store/hooks';
import posthog from 'posthog-js';
export function useScrollVisibilityTracking(
  sectionSelector = '.scroll-track-section'
) {
  const visibleSet = useRef(new Set());
  const timeoutRef = useRef(null);
  const observerRef = useRef(null);
  const mutationObserverRef = useRef(null);
  const { posthogEvents, scrollEventMap, buttonEventMap } = useStateFields(['posthogEvents', 'scrollEventMap', 'buttonEventMap']);

  useEffect(() => {

    if (!posthogEvents || Object.keys(posthogEvents).length === 0) return;

    const createIntersectionObserver = () => {
      const elements = document.querySelectorAll(sectionSelector);
      if (!elements.length) return false;

      observerRef.current = new IntersectionObserver(handleIntersections, { threshold: 0.5 });
      elements.forEach((el) => observerRef.current.observe(el));

      return true;
    };

    const handleIntersections = (entries) => {

      let didChange = false;
      entries.forEach((entry) => {
        const el = entry.target;
        const elKey = el.getAttribute("data-ph-key");
        if (!elKey) return;

        if (entry.isIntersecting) {
          if (!visibleSet.current.has(elKey)) {
            visibleSet.current.add(elKey);
            didChange = true;
          }
        } else {
          if (visibleSet.current.has(elKey)) {
            visibleSet.current.delete(elKey);
            didChange = true;
          }
        }
      });

      if (didChange) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          const visibleArray = Array.from(visibleSet.current);
          if (visibleArray.length === 0) return;

          const [pageKey] = visibleArray[0].split('|');
          let estimationId = null;
          const estimationProps =
            buttonEventMap?.[pageKey]?.[posthogEvents['quotation-section-visible']]?.props || [];

          if (estimationProps.includes("estimation_id")) {
            const firstEl = document.querySelector(
              `${sectionSelector}[data-ph-key="${visibleArray[0]}"]`
            );
            if (firstEl) {
              estimationId = firstEl.getAttribute("estimation_id");
            }
          }

          const sectionsPayload = visibleArray.map((elKey) => {
            const [pageKey, sectionName] = elKey.split('|');
            const el = document.querySelector(
              `${sectionSelector}[data-ph-key="${elKey}"]`
            );
            if (!el) return null;

            const propsToCapture = scrollEventMap?.[pageKey]?.[sectionName]?.props || [];
            const sectionData = { section_name: elKey };
            propsToCapture.forEach((prop) => {
              const val = el.getAttribute(prop);
              if (val !== null) sectionData[prop] = val;
            });

            return sectionData;
          }).filter(Boolean);

          const payload = { estimation_id: estimationId, sections: sectionsPayload };
          posthog.capture(posthogEvents['quotation-section-visible'], payload);
        }, 1000);
      }
    };

    // Try to create immediately; if elements missing, start watching DOM
    if (!createIntersectionObserver()) {
      mutationObserverRef.current = new MutationObserver(() => {
        const found = createIntersectionObserver();
        if (found && mutationObserverRef.current) {
          mutationObserverRef.current.disconnect();
          mutationObserverRef.current = null;
        }
      });
      mutationObserverRef.current.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      observerRef.current?.disconnect();
      mutationObserverRef.current?.disconnect();
      clearTimeout(timeoutRef.current);
    };
  }, [sectionSelector, posthogEvents, scrollEventMap, buttonEventMap]);
}
